package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.ShotAuditReportDto
import dev.lingvo.shared.ShotReportApprovalStatus
import dev.lingvo.shared.ShotReportParityStatus
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LKeyValueRow
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.browser.window
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.launch
import org.w3c.dom.events.Event

/** Separates private workspace delivery choices from explicitly created public aggregate reports. */
@Page("/app/projects/{slug}/screenshots/updates")
@Layout(".components.layouts.AppLayout")
@Composable
fun ScreenshotsUpdatesPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    val route = liveProjectRoute(slug)
    val subject = route?.authoritySubject(Session.organizations)
    var params by remember(slug) { mutableStateOf(readScreenshotsAreaState(ctx)) }
    val platform = params.platform
    val scope = rememberCoroutineScope()
    val runner = remember(subject, platform) {
        ScreenshotUpdatesRunner(slug, platform, { scope.launch { it() } }, currentPlatform = ::updatesWindowPlatform)
    }
    DisposableEffect(runner) { onDispose { runner.close() } }
    DisposableEffect(slug) {
        val pop: (Event) -> Unit = { params = params.copy(platform = updatesWindowPlatform()) }
        window.addEventListener("popstate", pop)
        onDispose { window.removeEventListener("popstate", pop) }
    }
    val person = Session.isAuthenticated && Session.token?.split('.')?.size == 3
    val admin = person && ProjectAuthority.resolve(route?.project, Session.organizations).canEditStructure
    Column(Modifier.fillMaxWidth().gap(Space.LG).styleModifier { property("min-width", "0"); property("max-width", "100%") }) {
        PageHeader(t("app.projects.slug.screenshots.updates.screenshot_updates", "Screenshot updates"),
            t("app.projects.slug.screenshots.updates.choose_how_you_hear_about_screenshot", "Choose how you hear about screenshot changes and share a read-only audit report."))
        ScreenshotsAreaNav(slug, ScreenshotsTab.UPDATES, params, { params = it })
        ScreenshotsOfflineState()
        key(subject, platform, person) {
            ScreenshotAuditReport(slug, subject, platform)
            if (person) {
                ScreenshotEmailPreferences(slug, subject, platform, runner)
                if (admin) {
                    ScreenshotWorkspaceNotifications(slug, subject, platform, runner)
                    ScreenshotReportShares(slug, subject, platform, runner)
                } else LMuted(t("app.projects.slug.screenshots.updates.workspace_delivery_and_public_report_links", "Workspace delivery and public report links are managed by an owner or admin."))
            } else LAlert(t("app.projects.slug.screenshots.updates.sign_in_with_a_person_session", "Sign in with a person session to change notification preferences or manage report links."))
        }
    }
}

/** Uses the live URL as well as composition state, closing the platform-change callback race. */
private fun updatesWindowPlatform(): StorePlatform {
    val query = js("new URLSearchParams(window.location.search)")
    return ScreenshotsAreaState.parse(mapOf("platform" to (query.get("platform") as? String))).platform
}

/** Every resource uses the verified project boundary, even when its storage scope is workspace-wide. */
internal suspend fun <T> readScreenshotUpdates(
    subject: ProjectAuthoritySubject?, slug: String, transport: suspend (VerifiedProjectRoute) -> T,
): T {
    val owned = subject ?: awaitCancellation()
    return requestProjectResource(owned.action, slug, Session.organizations, transport = transport)
}

@Composable
private fun ScreenshotAuditReport(slug: String, subject: ProjectAuthoritySubject?, platform: StorePlatform) {
    var reload by remember { mutableStateOf(0) }
    val loaded = loadReloadable(subject, platform, reloadToken = reload) {
        readScreenshotUpdates(subject, slug) { ScreenshotUpdatesApi.report(slug, platform) }
    }
    LSectionHeading(t("app.projects.slug.screenshots.updates.audit_report", "Audit report"),
        t("app.projects.slug.screenshots.updates.aggregate_counts_for_this_project_and", "Aggregate counts for this project and selected platform. Refresh to read the latest server state."),
        actions = { LButton(t("app.projects.slug.screenshots.updates.refresh_report", "Refresh report"), { reload++ },
            variant = LButtonVariant.SECONDARY, enabled = !loaded.refreshing) })
    loaded.refreshError?.let { AsyncFailureState(it, subject = t("app.projects.slug.screenshots.updates.the_audit_report", "the audit report"), onRetry = { reload++ }) }
    when (val state = loaded.state) {
        Async.Loading -> LoadingState(rows = 4)
        is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.updates.the_audit_report", "the audit report"), onRetry = { reload++ })
        is Async.Success -> ScreenshotAuditCounts(state.value)
    }
}

@Composable
private fun ScreenshotAuditCounts(report: ShotAuditReportDto) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
            LMuted(tf("screenshots.updates.generated_at", "Report generated: {when}", "when" to report.generatedAt))
            LKeyValueRow(t("app.projects.slug.screenshots.updates.planned_slots", "Planned slots"), report.coverage.slots.toString())
            LKeyValueRow(t("app.projects.slug.screenshots.updates.languages", "Languages"), report.coverage.locales.toString())
            LKeyValueRow(t("app.projects.slug.screenshots.updates.device_classes", "Device classes"), report.coverage.devices.toString())
            LKeyValueRow(t("app.projects.slug.screenshots.updates.required_captures", "Required captures"), report.coverage.cells.toString())
            LKeyValueRow(t("app.projects.slug.screenshots.updates.current_captures", "Current captures"), report.freshness.current.toString())
            LKeyValueRow(t("app.projects.slug.screenshots.updates.stale_captures", "Stale captures"), report.freshness.stale.toString())
            LKeyValueRow(t("app.projects.slug.screenshots.updates.missing_captures", "Missing captures"), report.freshness.missing.toString())
            val approval = when (report.approvals.status) {
                ShotReportApprovalStatus.NONE -> t("app.projects.slug.screenshots.updates.no_set_approval", "No set approval")
                ShotReportApprovalStatus.VALID -> t("app.projects.slug.screenshots.updates.valid_set_approval", "Valid set approval")
                ShotReportApprovalStatus.INVALID -> t("app.projects.slug.screenshots.updates.set_approval_needs_review", "Set approval needs review")
            }
            LKeyValueRow(t("app.projects.slug.screenshots.updates.approval", "Approval"), approval)
            LKeyValueRow(t("app.projects.slug.screenshots.updates.approval_items", "Approval items"), report.approvals.items.toString())
            LKeyValueRow(t("app.projects.slug.screenshots.updates.invalid_approval_items", "Invalid approval items"), report.approvals.invalidItems.toString())
            val parity = when (report.storeParity.status) {
                ShotReportParityStatus.UNKNOWN -> t("app.projects.slug.screenshots.updates.unknown", "Unknown")
                ShotReportParityStatus.VERIFIED -> t("app.projects.slug.screenshots.updates.verified_for_the_last_delivery_selection", "Verified for the last delivery selection")
                ShotReportParityStatus.MISMATCH -> t("app.projects.slug.screenshots.updates.a_mismatch_was_observed", "A mismatch was observed")
            }
            LKeyValueRow(t("app.projects.slug.screenshots.updates.store_parity", "Store parity"), parity)
            LKeyValueRow(t("app.projects.slug.screenshots.updates.sets_in_last_delivery", "Sets in last delivery"), report.storeParity.sets.toString())
            LKeyValueRow(t("app.projects.slug.screenshots.updates.verified_sets", "Verified sets"), report.storeParity.verifiedSets.toString())
            LKeyValueRow(t("app.projects.slug.screenshots.updates.mismatched_sets", "Mismatched sets"), report.storeParity.mismatchedSets.toString())
            LKeyValueRow(t("app.projects.slug.screenshots.updates.unknown_sets", "Unknown sets"), report.storeParity.unknownSets.toString(), hairline = false)
            LMuted(report.storeParity.checkedAt?.let { tf("screenshots.updates.parity_checked", "Store state checked: {when}", "when" to it) }
                ?: t("app.projects.slug.screenshots.updates.no_store_read_back_has_been", "No store read-back has been recorded."))
            LMuted(t("app.projects.slug.screenshots.updates.parity_covers_the_last_delivery_selection", "Parity covers the last delivery selection. It does not certify the entire live store listing."))
        }
    }
}
